import { pick } from "@bonliva-crm/common";
import locale from "@bonliva-crm/locale";
import { DrivingLicenceType, LegalBasis } from "@bonliva-crm/shared";
import { Option } from "@common/Select";
import { ConsultantsQueryInput, ExpertiseFragment } from "@graphql";
import { createExpertiseInput } from "@search/Utils";
import { store } from "@store";
import { compact } from "lodash";
import moment, { Moment } from "moment";
import { extendMoment, MomentRange } from "moment-range";
import { Filter } from "./ConsultantSearch/utils";

export type PriceOption = Option & { archived?: boolean };
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const momentRange: MomentRange & moment.Moment = extendMoment(moment);

/**
 * Form search data to graphql variables
 */
export function getConsultantsFilter(
  search: Filter,
  opts: {
    areasOfExp: ExpertiseFragment[];
    includeAvailabilityDateRange?: boolean;
    alphabetFilter?: string[];
  }
): ConsultantsQueryInput {
  const {
    lastContact: [to, from]
  } = search;

  const birthdate =
    search.ageRange?.from && search.ageRange?.to
      ? ageToDates(search.ageRange.from, search.ageRange.to)
      : null;

  const dateRange2 =
    from || to
      ? {
          from,
          to
        }
      : null;

  const isValid = parseStringBool(search.isValid);

  const dates: Partial<ConsultantsQueryInput> = search.myActivity
    ? {
        lastContactUserId: [store().user.id],
        lastContactDate: dateRange2
      }
    : {
        lastContactDate: dateRange2
      };

  return {
    ...pick(
      search,
      "callListIds",
      "initialContact",
      "competences",
      "competitors",
      "lastContactType",
      "lastContactSelectType",
      "specializations",
      "status",
      "tags",
      "team",
      "text"
    ),
    ...dates,
    isValid,
    areasOfExpertise: createExpertiseInput(
      search.areasOfExpertiseProfessions,
      opts.areasOfExp
    ),
    areasOfExpertiseExperience: search.areasOfExpertiseExperience
      ? parseInt(search.areasOfExpertiseExperience)
      : 0,
    availabilityDateRange:
      search.availability?.from && search.availability?.to ? search.availability : null,
    availabilityRegions: search.regions,
    availabilityWorkshifts: search.workshifts,
    // disable location filter if $text filter is Enabled (mongo limitations)
    // EXCLUDE from getConsultantsByWeeksFilter:
    //     bookingStatus,
    //     bookingStatusDate,
    //     bookingStatusDateEnd,
    //     bookingStatusOption,
    //     nationalQualification,
    bookingStatusOption: search.bookingStatusOption,
    bookingStatus: search.bookingStatus,
    bookingStatusDate: search.bookingStatusDate,
    bookingStatusDateEnd: search.bookingStatusDateEnd,
    customerIds: search.customerIds?.map((x) => x.value) ?? null,
    location:
      search.searchType === "extended" ? { zipOrCity: "", klm: "0" } : search.location,
    availableForWorkInCountries: search.availableForWorkInCountries,
    birthdate,
    fullTextSearch: search.searchType === "extended",
    gdprMarketing: search.gdprMarketing,
    gdprIntegrationPolicy: search.gdprIntegrationPolicy,
    gdprJobMailingEmail: search.gdprJobMailingEmail,
    gdprJobMailingSms: search.gdprJobMailingSms,
    legalBasis: search.legalBasis === "null" ? null : (search.legalBasis as LegalBasis),
    hprNumber: parseStringBool(search.hprNumber),
    workedInNorway: search.workedInNorway,
    approvedAuthorization: search.approvedAuthorization,
    norwayConsultantStatus: search.norwayConsultantStatus,
    participatesInVaccination: parseStringBool(search.participatesInVaccination),
    hasDrivingLicence: search.hasDrivingLicence ? DrivingLicenceType.Manual : undefined,
    responsiblePersonId: search.responsiblePerson,
    qualification: search.qualification,
    nationalQualification: search.nationalQualification,
    specializations: search.specializations,
    systems: search.knownSystems,
    fitness: search.fitness,
    professionsForm: search.professionsForm,
    alphabetFilter: compact(opts.alphabetFilter),
    wageRange: search.wagesRange,
    hasLegalPersonType: search.hasLegalPersonType,
    hasEmail: search.hasEmail,
    hasPhone: search.hasPhone,
    leadStatus: search.leadStatus,
    interested: search.interested,
    experienced: search.experienced
  };
}

function parseStringBool(str: string) {
  return str === "true" ? true : str === "false" ? false : null;
}

function ageToDates(from: number, to: number) {
  // basedate: someone who becomes X years old today might have been born X year + 364 days ago!
  const baseDate = moment.utc().startOf("day").subtract(1, "year").add(1, "day");

  return {
    from: baseDate.clone().subtract(to, "years").toDate(),
    to: moment.utc().startOf("day").subtract(from, "years").toDate()
  };
}

export function checkArchivedPriceList(
  priceList: { startDate?: Date | Moment; endDate?: Date | Moment },
  contract: { StartDate?: Date | Moment; EndDate?: Date | Moment }
) {
  const { StartDate, EndDate } = contract;
  const { startDate, endDate } = priceList;
  const hasRangeDate = StartDate || EndDate;

  if (!hasRangeDate) {
    return false;
  }

  const priceListRange = momentRange.range(moment(startDate), moment(endDate));

  const contractRange = momentRange.range(
    StartDate ? moment(StartDate) : moment(EndDate),
    EndDate ? moment(EndDate) : moment(StartDate)
  );

  const inRange = priceListRange.contains(contractRange);

  return !inRange;
}

export function sortPriceOptions<T extends PriceOption[]>(arr: T): any[] {
  const archivedParentValue = "archived";

  const hasArchived = arr.some((a) => a.archived);

  if (!hasArchived) {
    return arr;
  }

  const newArr = [
    ...arr,
    {
      value: archivedParentValue,
      label: `${locale.Archived}:`,
      archived: false,
      unselectable: true
    }
  ].map((item) => ({
    ...item,
    parent: item.archived ? archivedParentValue : null
  }));

  return newArr;
}

export function getExpandedArchivedGroup(val: string, options: PriceOption[]) {
  return Boolean(val && options.find(({ value, archived }) => value === val && archived));
}

// specialization = "name (group)";
export function parseSpec(specialization: string) {
  if (!specialization.includes("(")) {
    return {
      label: specialization,
      value: specialization
    };
  }

  const [name, firstOccur, secondOccur] = specialization
    .slice(0, specialization.length - 1)
    .split(" (");
  const label = secondOccur ? `${name} (${firstOccur}` : name;

  return {
    label: label,
    value: specialization,
    parent: secondOccur ?? firstOccur
  };
}
